import { useEffect, useState } from "react";
import { IndianRupee, AlertTriangle, Landmark } from "lucide-react";
import { toast } from "sonner";
import { appApi, inr, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;

export default function Cod() {
  const [data, setData] = useState(null);
  const [bank, setBank] = useState(null);
  const [bankBusy, setBankBusy] = useState(false);

  useEffect(() => {
    appApi.get("cod/summary").then(setData).catch(() => setData({ error: true }));
    appApi.get("user/bank-details")
      .then((d) => setBank(d.bank_details || {}))
      .catch(() => setBank({}));
  }, []);

  const setB = (k) => (e) => {
    let v = e.target.value;
    if (k === "account_number") v = v.replace(/\s/g, "");
    if (k === "ifsc") v = v.toUpperCase().replace(/\s/g, "").slice(0, 11);
    setBank((b) => ({ ...b, [k]: v }));
  };

  const saveBank = async (e) => {
    e.preventDefault();
    if (!IFSC_RE.test(bank?.ifsc || "")) {
      toast.error("Enter a valid IFSC code (e.g. HDFC0001234).");
      return;
    }
    setBankBusy(true);
    try {
      await appApi.put("user/bank-details", {
        account_holder_name: bank.account_holder_name || "",
        account_number: bank.account_number || "",
        ifsc: bank.ifsc || "",
        bank_name: bank.bank_name || "",
      });
      toast.success("Bank details saved — COD payouts will land here.");
      appApi.get("cod/summary").then(setData).catch(() => {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBankBusy(false);
    }
  };

  if (!data)
    return <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-400" data-testid="cod-loading">Loading COD summary…</p>;

  return (
    <div data-testid="cod-page" className="space-y-6">
      <div>
        <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">COD</p>
        <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">COD Remittance</h1>
      </div>

      {bank && !bank.account_number && (
        <div data-testid="cod-bank-warning" className="border-2 border-obsidian bg-acid px-5 py-4 flex items-center gap-3 font-semibold text-sm text-white">
          <AlertTriangle className="w-5 h-5" />
          Add your bank details below to receive COD payouts on time.
        </div>
      )}

      {bank && (
        <form onSubmit={saveBank} className="border-2 border-obsidian bg-white p-6 max-w-3xl space-y-4" data-testid="cod-bank-form">
          <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500 flex items-center gap-2">
            <Landmark className="w-4 h-4 text-blaze" /> Payout bank account
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <input required value={bank.account_holder_name || ""} onChange={setB("account_holder_name")}
              placeholder="Account holder name" data-testid="bank-holder"
              className="border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
            <input required inputMode="numeric" maxLength={25} value={bank.account_number || ""} onChange={setB("account_number")}
              placeholder="Bank account number" data-testid="bank-account"
              className="border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
            <input required maxLength={11} value={bank.ifsc || ""} onChange={setB("ifsc")}
              placeholder="IFSC (e.g. HDFC0001234)" data-testid="bank-ifsc"
              className="border-2 border-obsidian bg-paper px-4 py-2.5 text-sm uppercase outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
            <input required value={bank.bank_name || ""} onChange={setB("bank_name")}
              placeholder="Bank name" data-testid="bank-name"
              className="border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
          </div>
          <button type="submit" disabled={bankBusy} data-testid="bank-save"
            className="bg-obsidian text-sky-300 font-bold px-8 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_rgba(37,99,235,0.4)] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150 disabled:opacity-60">
            {bankBusy ? "Saving…" : "Save Bank Details"}
          </button>
        </form>
      )}

      <div className="grid grid-cols-2 border-2 border-obsidian bg-white max-w-2xl">
        <div className="p-6 border-r-2 border-obsidian" data-testid="cod-pending-total">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Pending</p>
            <IndianRupee className="w-4 h-4 text-zinc-400" />
          </div>
          <p className="font-display font-black text-3xl tracking-tight mt-2">{inr(data.total_pending_inr)}</p>
        </div>
        <div className="p-6" data-testid="cod-settled-total">
          <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Settled</p>
          <p className="font-display font-black text-3xl tracking-tight mt-2">{inr(data.total_settled_inr)}</p>
        </div>
      </div>

      <div className="border-2 border-obsidian bg-white overflow-x-auto">
        <p className="px-5 py-4 border-b-2 border-obsidian text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Pending COD orders</p>
        <table className="w-full text-sm min-w-[560px]" data-testid="cod-pending-table">
          <tbody>
            {(data.pending_orders || []).map((o, i) => (
              <tr key={o.order_id || o.id || i} className="border-b border-zinc-100 last:border-0 hover:bg-acid/20 transition-colors duration-100">
                <td className="px-5 py-3 font-mono text-xs font-bold">{o.awb || o.order_id || "—"}</td>
                <td className="px-5 py-3 text-zinc-600">{o.courier_name || ""}</td>
                <td className="px-5 py-3 font-mono text-xs text-zinc-500">{fmtDate(o.delivered_at || o.created_at)}</td>
                <td className="px-5 py-3 text-right font-mono text-xs font-bold">{inr(o.cod_amount_inr ?? o.amount_inr ?? o.cod_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {(data.pending_orders || []).length === 0 && (
          <p className="px-5 py-10 text-sm text-zinc-500" data-testid="cod-empty">No pending COD — all settled. <StatusBadge status="resolved" /></p>
        )}
      </div>
    </div>
  );
}
